import { useMachineSettingsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineSettingsDialogState';
import {
  MachineSettingsQueryParams,
  MachineSettingsSection,
} from 'enums/machine-settings';
import {
  ErrorTableStaticValues,
  IErrorLevelWithNA,
  IErrorTableFilters,
} from 'interfaces/i-machine-errors-table';
import {
  IMachineSettingsDateFilter,
  IMachineSettingsURLParams,
} from 'interfaces/i-machine-settings';

export const useMachineErrorsDialogState = () => {
  const { getParams, setParams } = useMachineSettingsDialogState();

  const searchParams = getParams();
  const errorID = searchParams.get(MachineSettingsQueryParams.ErrorID);

  // e.g. &errorSender=PosController&errorSlack=true&errorDateStart=2023-01-01&errorDateEnd=2023-01-31&errorLevel=info&errorInternal=true&errorID=some-error-type
  const errorSender = searchParams.get(MachineSettingsQueryParams.ErrorSender);
  const errorSlack = searchParams.get(MachineSettingsQueryParams.ErrorSlack);
  const errorLevel = searchParams.getAll(MachineSettingsQueryParams.ErrorLevel); // array
  const errorTypeID = searchParams.get(MachineSettingsQueryParams.ErrorTypeID);

  const errorInternal = searchParams.get(
    MachineSettingsQueryParams.ErrorInternal
  );
  const errorDateStart = searchParams.get(
    MachineSettingsQueryParams.ErrorDateStart
  );
  const errorDateEnd = searchParams.get(
    MachineSettingsQueryParams.ErrorDateEnd
  );

  const errorFilters: IErrorTableFilters = {
    slack: (errorSlack as ErrorTableStaticValues) || '',
    internal: (errorInternal as ErrorTableStaticValues) || '',
    level: (errorLevel as IErrorLevelWithNA[]) || [],
    sender: errorSender || '',
    errorTypeID: errorTypeID || '',
    dateRange:
      errorDateStart && errorDateEnd
        ? {
            start: new Date(errorDateStart),
            end: new Date(errorDateEnd),
          }
        : undefined,
  };

  const setErrorID = (id: string) => {
    setParams(
      {
        [MachineSettingsQueryParams.ErrorID]: id,
      },
      'setErrorID'
    );
  };

  const resetErrorID = () => {
    setParams(
      {
        [MachineSettingsQueryParams.ErrorID]: undefined,
      },
      'resetErrorID'
    );
  };

  const setErrorFilter = (v: Partial<IErrorTableFilters>) => {
    const updated: Partial<IMachineSettingsURLParams> = {};

    for (const k in v) {
      // type-safe iteration over keys
      const key = k as keyof IErrorTableFilters;
      const value = v[key];

      switch (key) {
        case 'dateRange': {
          if (!value) {
            updated[MachineSettingsQueryParams.ErrorDateStart] = undefined;
            updated[MachineSettingsQueryParams.ErrorDateEnd] = undefined;
            break;
          }

          const dateRange = value as IMachineSettingsDateFilter;

          updated[MachineSettingsQueryParams.ErrorDateStart] =
            dateRange.start.toISOString();
          updated[MachineSettingsQueryParams.ErrorDateEnd] =
            dateRange.end.toISOString();
          break;
        }

        case 'slack':
          if (!value) {
            updated[MachineSettingsQueryParams.ErrorSlack] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ErrorSlack] =
            value as ErrorTableStaticValues;
          break;

        case 'internal':
          if (!value) {
            updated[MachineSettingsQueryParams.ErrorInternal] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ErrorInternal] =
            value as ErrorTableStaticValues;
          break;

        case 'sender':
          if (!value) {
            updated[MachineSettingsQueryParams.ErrorSender] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ErrorSender] = value as string;
          break;

        case 'errorTypeID':
          if (!value) {
            updated[MachineSettingsQueryParams.ErrorTypeID] = undefined;
            break;
          }

          updated[MachineSettingsQueryParams.ErrorTypeID] = value as string;
          break;

        case 'level':
          updated[MachineSettingsQueryParams.ErrorLevel] =
            value as IErrorLevelWithNA[];
          break;

        default:
          break;
      }
    }

    setParams(updated, 'setErrorFilter');
  };

  // convenience function to set both active section and error filter
  const viewSenderErrors = (sender: string) => {
    setParams(
      {
        [MachineSettingsQueryParams.SectionID]: MachineSettingsSection.Errors,
        [MachineSettingsQueryParams.ErrorSender]: sender,
        [MachineSettingsQueryParams.ErrorID]: undefined,
      },
      'viewSenderErrors'
    );
  };

  return {
    errorID,
    setErrorID,
    resetErrorID,
    errorSender,
    errorFilters,
    setErrorFilter,
    viewSenderErrors,
  };
};
